// © 2015-2021 WDNA - All Rights Reserved

import * as React from 'react';
import _ from 'lodash';
import Slider from 'react-slick';

import ContainerModal from 'wdna-components/lib/modals/generics/ContainerModal';
import { initProcess, finishProcess } from 'wdna-components/lib/scheduler/actions';
import { messageError } from 'wdna-components/lib/helpers/notification';
import { getErrorMessage } from 'wdna-components/lib/helpers/response';

import { ContextType, FilterContext } from '../../../../../types';
import { sensor, AlertDO, waste, RegisterInfoDO, GenericDO } from '../../../../sensors/requests';

import { GraphCircleType } from './GraphCircle';
import { GraphChartType } from './GraphChart';

const GraphChart: React.LazyExoticComponent<GraphChartType> = React.lazy(() => import('./GraphChart'));
const GraphCircle: React.LazyExoticComponent<GraphCircleType> = React.lazy(() => import('./GraphCircle'));

const containerCode: RegExp[] = [
  /^(SUW-002-.{8})$/, /^(FOR-002-.{8})$/, /^(PAP-002-.{8})$/, /^(PET-002-.{8})$/, /^(GLA-002-.{8})$/, /^(PNG-002-.{8})$/, /^(CRK-002-.{8})$/
];
const containerCode2: RegExp[] = [
  /^(SUW-0[01][01]-.{8})$/, /^(FOR-0[01][01]-.{8})$/, /^(PAP-0[01][01]-.{8})$/, /^(PET-0[01][01]-.{8})$/, /^(GLA-0[01][01]-.{8})$/, /^(PNG-0[01][01]-.{8})$/,
  /^(CRK-0[01][01]-.{8})$/
];

type DashType = {
  props: {
    title: string;
    container: string;
    className: string;
  };
}

const dashConfig: DashType[] = [
  {
    props: {
      title: 'Urban Solid',
      container: 'SUW',
      className: 'waste-container',
    },
  },
  {
    props: {
      title: 'Organic',
      container: 'FOR',
      className: 'waste-container',
    },
  },
  {
    props: {
      title: 'Paper',
      container: 'PAP',
      className: 'waste-container',
    },
  },
  {
    props: {
      title: 'Light packaging',
      container: 'PET',
      className: 'waste-container',
    },
  },
  {
    props: {
      title: 'Glass',
      container: 'GLA',
      className: 'waste-container',
    },
  },
];

const cssstyle: string = '';

interface Props {
  rowId?: number;
}

interface State {
  mainFilter?: any;

  collapsed: boolean;
  modalShow: boolean;
  modalData: any;
  sensorNameList: any[];

  arrayWeight: any[];
  arrayBattery: any[];
  weightCount: number;
  batteryCount: number;
}

export default class MainFrame extends React.Component<Props, State> {
  private pid: string;
  private mounted: boolean;

  public static contextType: React.Context<ContextType> = FilterContext;
  public context: React.ContextType<typeof FilterContext> | undefined;

  public constructor(props: Props) {
    super(props);

    this.pid = window.getStr();
    this.mounted = false;

    this.state = {
      collapsed: false,
      modalShow: false,
      modalData: {},
      sensorNameList: [],

      arrayWeight: [],
      arrayBattery: [],
      weightCount: 0,
      batteryCount: 0,
    };
  }

  public componentDidMount = (): void => {
    this.mounted = true;
    this.loadInfoAlerts().catch(() => { /* pass */ });
    setInterval(() => { this.loadInfoAlerts().catch(() => { /* pass */ }); }, 300000);
  }

  public componentDidUpdate(): void {
    const newFilter: any = this.context?.parentState.mainFilter;
    const oldFilter: any = this.state.mainFilter;

    if (oldFilter !== newFilter && !_.isEmpty(newFilter)) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        mainFilter: newFilter,
      }, () => {
        this.loadInfoAlerts().catch(() => { /* pass */ });
      });
    }
  }

  public componentWillUnmount = (): void => {
    this.mounted = false;
  }

  public loadInfoAlerts = async (): Promise<void> => {
    // Request
    const mainFilter: any = this.context?.parentState?.mainFilter;
    const companyId: string = mainFilter.company;
    const req: Record<string, any> = {};
    if (!_.isEmpty(companyId) && companyId !== '0') {
      req.companyId = [companyId];
    }

    this.loadingStart();
    try {
      const sensorAlerts: AlertDO[] = await sensor<AlertDO[]>('alerts') || [];
      sensorAlerts.forEach((itemId: AlertDO) => {
        const itemName: AlertDO = itemId;
        if (itemName.code === 'FIRE') {
          const { sensors } = itemName;
          this.setState({
            sensorNameList: sensors.map((item: GenericDO) => {
              return `${item.name} - ${item.description}`;
            }),
          });
        }
      });

      let weightCount: number = 0;
      let batteryCount: number = 0;
      const { arrayWeight, arrayBattery } = this.state;

      const temperature2W: Record<string, number[]> = {};
      const temperature4W: Record<string, number[]> = {};

      const dataV3: RegisterInfoDO[] = await waste<RegisterInfoDO[]>('weights', undefined, req) || [];
      dataV3.forEach((e: RegisterInfoDO) => {
        const type: string = e.values.containerType;
        const model: string = e.values.containerModel;

        if (e.values.weightAlarm === 1) {
          arrayWeight.push(`${e.sensorName} - ${e.sensorDescription}`);
          weightCount += 1;
        }
        if (e.values.batteryAlarm === 1) {
          arrayBattery.push(`${e.sensorName} - ${e.sensorDescription}`);
          batteryCount += 1;
        }

        if (!(type in temperature2W)) temperature2W[type] = [];
        if (!(type in temperature4W)) temperature4W[type] = [];
        if (model === '2-WHEELS') temperature2W[type].push(e.values.temperatureCur);
        if (model === '4-WHEELS') temperature4W[type].push(e.values.temperatureCur);
      });

      this.context?.updateParent({
        temperature2W,
        temperature4W
      });

      this.setState({
        weightCount,
        arrayWeight,
        batteryCount,
        arrayBattery,
      });
    } catch (error) {
      messageError('Error', getErrorMessage(error));
    }
    this.loadingFinish();
  };

  public loadingStart = (): void => {
    window.store.dispatch(initProcess(this.pid));
  };

  public loadingFinish = (): void => {
    window.store.dispatch(finishProcess(this.pid));
  };

  public changeClass = (): void => {
    this.setState((prevState: State) => ({
      collapsed: !prevState.collapsed
    }));
  };

  public onModalOpen = (value: any): void => {
    this.setState({
      modalShow: true,
      modalData: value,
    });
  };

  public onModalClose = (): void => {
    this.setState({
      modalShow: false,
    });
  };

  public updOtherSubtype = (): void => {
    const circleData: any[] = this.context?.parentState?.dashboardData?.circleData || [];
    if (!this.existsOtherSubtype() && circleData) {
      circleData.forEach((item: any) => {
        if (item.name === 'PNG') {
          dashConfig.push({
            props: {
              title: 'Pruning',
              container: 'PNG',
              className: 'waste-container',
            },
          });
        }
        if (item.name === 'CRK') {
          dashConfig.push({
            props: {
              title: 'Crockery',
              container: 'CRK',
              className: 'waste-container',
            },
          });
        }
      });
    }
  }

  public existsOtherSubtype = (): number => {
    let count: number = 0;
    dashConfig.forEach((item: DashType) => {
      if (item.props.container === 'PNG') count += 1;
      if (item.props.container === 'CRK') count += 1;
    });

    return count;
  }

  public render(): React.ReactNode[] {
    this.updOtherSubtype();
    this.existsOtherSubtype();
    const parentClass: string = this.state.collapsed ? 'container-alarms global collapsed' : 'container-alarms global';
    const { modalShow, modalData, sensorNameList } = this.state;
    let selectStyle: string = 'containers_container';
    if (this.existsOtherSubtype() === 1) selectStyle = 'containers_container_with_subtype1';
    if (this.existsOtherSubtype() === 2) selectStyle = 'containers_container_with_subtype2';
    const { weightCount, batteryCount, arrayWeight, arrayBattery } = this.state;
    const settings: any = {
      dots: true,
      infinite: true,
      arrows: false,
      speed: 500,
      slidesToShow: 5,
      slidesToScroll: 1,
      responsive: [
        {
          breakpoint: 4000,
          settings: 'unslick',
        },
        {
          breakpoint: 1460,
          settings: {
            slidesToShow: 4,
            slidesToScroll: 1,
          },
        },
        {
          breakpoint: 1124,
          settings: {
            slidesToShow: 3,
            slidesToScroll: 1,
          },
        },
        {
          breakpoint: 900,
          settings: {
            slidesToShow: 2,
            slidesToScroll: 1,
          },
        },
        {
          breakpoint: 600,
          settings: {
            slidesToShow: 1,
            slidesToScroll: 1,
          },
        },
      ],
    };

    return [
      <div>
        <div id="tab-main-content">
          {/* <div className={parentClass}>
            <div className="hide-container-alarms" onClick={this.changeClass} />
            {sensorNameList.length !== 0 && (
              <div className="alarm fire-alarm" onClick={this.onModalOpen}>
                <div className="bullet">
                  <span>{sensorNameList.length}</span>
                </div>
                <div className="icon" />
              </div>
            )}
            {(weightCount !== 0 && weightCount !== undefined) && (
              <div className="alarm weight-alarm" onClick={this.onModalOpen}>
                <div className="bullet"><span>{weightCount}</span></div>
                <div className="icon" />
              </div>
            )}
            {(batteryCount !== 0 && batteryCount !== undefined) && (
              <div className="alarm default-alarm" onClick={this.onModalOpen}>
                <div className="bullet"><span>{batteryCount}</span></div>
                <div className="icon" />
              </div>
            )}
          </div> */}

          <ContainerModal
            fullScreenButton={false}
            show={modalShow}
            title={`ACTIVE ALARMS - ${modalData && modalData.id}`}
            modalClass="selector-modal"
            onClose={this.onModalClose}
            footerButtons={[
              {
                text: 'OK',
                className: 'btn btn-primary',
                onClick: this.onModalClose,
              },
            ]}
          >
            <div className="modal-alarms">
              <div className="alarms-modal-header">
                <h1>Active Alarms</h1>
                <div className="close close-modal" />
              </div>
              <div className="alarms-modal-content">
                <div className="fire-alarms alarms-list">
                  <h2>Fire alarms</h2>
                  <ul>
                    {sensorNameList.map((name: any) => {
                      return (
                        <li>
                          <span className="sensor-code">{name}</span>
                          {/* <span className="container-type" /> */}
                          {/* <span className="sensor-name" /> */}
                        </li>
                      );
                    })}
                  </ul>
                </div>
                <div className="overweight-alarms alarms-list">
                  <h2>Overweight alarms</h2>
                  <ul>
                    {arrayWeight.map((name: any) => {
                      return (
                        <li>
                          <span className="sensor-code">{name}</span>
                          {/* <span className="container-type" />
                          <span className="sensor-name" /> */}
                        </li>
                      );
                    })}
                  </ul>
                </div>
                <div className="undefined-alarms alarms-list">
                  <h2>Low battery</h2>
                  <ul>
                    {arrayBattery.map((name: any) => {
                      return (
                        <li>
                          <span className="sensor-code">{name}</span>
                          {/* <span className="container-type" />
                          <span className="sensor-name" /> */}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>
            </div>
          </ContainerModal>
        </div>
        <link
          rel="stylesheet"
          type="text/css"
          charSet="UTF-8"
          href="https://cdnjs.cloudflare.com/ajax/libs/slick-carousel/1.6.0/slick.min.css"
        />
        <link
          rel="stylesheet"
          type="text/css"
          href="https://cdnjs.cloudflare.com/ajax/libs/slick-carousel/1.6.0/slick-theme.min.css"
        />
        <style>{cssstyle}</style>
        <Slider {...settings} className={(selectStyle)}>
          {dashConfig.map((x: DashType, i: number) => {
            return (
              <div key={x.props.container}>
                <GraphCircle {...x.props} code1={containerCode[i]} code2={containerCode2[i]} />
              </div>
            );
          })}
        </Slider>
      </div>,
      <div>
        <GraphChart />
      </div>
    ];
  }
}
